import { useCallback, useEffect, useState } from "react";
// import { OverlayTrigger } from "react-bootstrap";
import type { CellProps, Column } from "react-table";

import { ActionCell } from "../../../../components/ActionCell/ActionCell";
import {
  DateColumn,
  DefaultColumn,
} from "../../../../components/CustomizedTable/Columns";
import { PaginatedTable } from "../../../../components/CustomizedTable/PaginatedTable";
import { DeleteCacheItemPopup } from "../../../../components/DeleteCacheItemPopup/DeleteCacheItemPopup";
import {
  ShieldAttachIcon,
  // StopIcon,
  // UserIcon,
} from "../../../../components/icons";
// import { renderTooltip } from "../../../../components/StyledTooltip/StyledTooltip";
import { TableCellSm, TableLineSm } from "../../../../components/TableLineSm";
import { useMediaQueryContext } from "../../../../contexts/mediaQueryProvider/MediaQueryProvider";
import { useOrganizationContext } from "../../../../contexts/organizations/organizationsContext";
import { useDeleteCacheItem } from "../../../../hooks/reactQueryHooks/useCacheItems";
import type { IData } from "../../../../types";
interface IProps {
  items: IData[];
  totalItems?: number;
  isLoading?: boolean;
}

const ShieldTable: React.FC<IProps> = ({
  items,
  totalItems = 0,
  isLoading = false,
}) => {
  const { currentOrganization } = useOrganizationContext();
  const { isLg } = useMediaQueryContext();

  const [rowToDelete, setRowToDelete] = useState<{
    itemId: string;
    projId: string;
  } | null>(null);

  const [confirmModalVisible, setConfirmModalVisible] = useState(false);

  const deleteMutation = useDeleteCacheItem();

  const handleCancelDelete = () => {
    setConfirmModalVisible(false);
  };

  const handleDelete = () => {
    if (rowToDelete) {
      deleteMutation.mutate({
        cacheItemId: rowToDelete.itemId,
        organizationId: currentOrganization?.id || "",
        applicationId: rowToDelete.projId,
      });
    }
  };

  useEffect(() => {
    if (deleteMutation.isSuccess) {
      setConfirmModalVisible(false);
    }
  }, [deleteMutation.isSuccess]);

  const renderReason = useCallback(() => {
    return (
      <div className="d-flex w-auto ">
        <span className="svg-icon pr-2">
          <ShieldAttachIcon size={16} />
        </span>
        <span className="px-2">Prompt injection</span>
      </div>
    );
  }, []);
  // todo: api not implemented
  // const renderUser = (props: CellProps<IData>) => {
  //   return (
  //     <div className="d-flex w-auto">
  //       <span className="svg-icon">
  //         <UserIcon size={16} />
  //       </span>
  //       <span className="mx-2">{props.row.original?.id}</span>
  //       <OverlayTrigger
  //         placement="bottom"
  //         overlay={renderTooltip(
  //           "User blocked automatically due to suspicious activity",
  //         )}
  //       >
  //         <span className="cursor-pointer">
  //           <StopIcon size={16} />
  //         </span>
  //       </OverlayTrigger>
  //     </div>
  //   );
  // };

  const renderActions = useCallback(
    (props: CellProps<IData>) => {
      return (
        <ActionCell
          onEditClick={() => {}}
          onDeleteClick={() => {
            setRowToDelete(props?.row?.original?.id);
            setConfirmModalVisible(true);
            deleteMutation.reset();
          }}
        />
      );
    },
    [deleteMutation],
  );

  const renderPrompt = useCallback(({ row }: CellProps<IData>) => {
    const messages = row.original.request.body?.messages;
    const content = messages[messages.length - 1].content;

    return <DefaultColumn data={content} />;
  }, []);

  const renderDate = useCallback((props: CellProps<IData>) => {
    return (
      <DateColumn date={props.row.original.request.timestamp} type="long" />
    );
  }, []);

  const renderSM = useCallback(
    (props: CellProps<IData>) => {
      const messages = props.row.original.request.body?.messages;
      const content = messages[messages.length - 1].content;
      return (
        <TableLineSm
          title={
            <div className="d-flex w-auto align-items-center">
              <DefaultColumn data={content} />
            </div>
          }
          controls={renderActions(props)}
          colspan={2}
        >
          <TableCellSm title="Blocked reason">{renderReason()}</TableCellSm>
          <TableCellSm title="Date">
            <DateColumn
              date={props.row.original.request.timestamp}
              type="long"
            />
          </TableCellSm>
        </TableLineSm>
      );
    },
    [renderActions, renderReason],
  );

  const columns: Column<IData>[] = [
    {
      Header: "Blocked Prompt",
      accessor: "prompt",
      minWidth: 150,
      width: "50%",
      Cell: renderPrompt,
    },
    {
      Header: "Blocked reason",
      accessor: "reason",
      minWidth: 170,
      Cell: renderReason,
    },
    {
      Header: "Date",
      accessor: "date",
      Cell: renderDate,
    },
    {
      Header: "",
      id: "edit",
      minWidth: 50,
      width: 50,
      maxWidth: 50,
      Cell: renderActions,
    },
  ];

  const columnsSm: Column<IData>[] = [
    {
      Header: "Blocked Prompt",
      accessor: "prompt",
      Cell: renderSM,
    },
  ];

  return (
    <>
      {confirmModalVisible && (
        <DeleteCacheItemPopup
          onCancel={handleCancelDelete}
          onSubmit={handleDelete}
          isProcessing={deleteMutation.isLoading}
          isError={deleteMutation.isError}
        />
      )}

      <PaginatedTable
        columns={isLg ? columnsSm : columns}
        data={items}
        totalItems={totalItems}
        isLoading={isLoading}
      />
    </>
  );
};

export default ShieldTable;
